import { Inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { IMooGlobal, mooGlobalInjectionToken } from "moo-shared";
import { Observable, Subject } from "rxjs";

export const GoogleOAuthErrors = {
  NO_ACCESS_TOKEN: "NO_ACCESS_TOKEN",
  GOOGLE_LOGIN_FAILED: "GOOGLE_LOGIN_FAILED",
  USERINFO_REQUEST_FAILED: "USERINFO_REQUEST_FAILED",
  EMAIL_MISMATCH: "EMAIL_MISMATCH",
  UNKNOWN: "UNKNOWN"
} as const;

export type GoogleOAuthErrorCode = typeof GoogleOAuthErrors[keyof typeof GoogleOAuthErrors];


export interface GoogleOAuthError {
  code: GoogleOAuthErrorCode;
  message: string;
  details?: string;
}

export interface GoogleOAuthLoginRequest {
  loginName: string;
  oldPassword?: string;
  newPassword?: string;
}

export interface GoogleOAuthCallbackData {
  accessToken: string | null;
  error: string | null;
  errorDescription: string | null;
  loginName: string;
  oldPassword: string;
  newPassword: string;
}

export interface GoogleOAuthCallbackResult {
  accessToken: string;
  loginName: string;
  oldPassword: string;
  newPassword: string;
}

@Injectable({ providedIn: "root" })
export class GoogleOAuthService {

  private static readonly TRANSLATION_PREFIX = "google-oauth-errors";

  private isBusy = false;

  constructor(
    private readonly translateService: TranslateService,
    @Inject(mooGlobalInjectionToken) private readonly moo: IMooGlobal
  ) {}

  /**
   * Redirects to Google OAuth login page.
   */
  login(request: GoogleOAuthLoginRequest): void {
    if (this.isBusy) {
      return;
    }

    this.isBusy = true;

    const redirectUri = `${window.location.origin}/login/update-password`;

    const params = new URLSearchParams({
      client_id: (this.moo as any).GoogleAuthClientId,
      redirect_uri: redirectUri,
      response_type: "token",
      scope: "openid profile email",
      login_hint: request.loginName,
      state: JSON.stringify({ loginName: request.loginName, originalUrl: window.location.href, ...(request.oldPassword && { oldPassword: request.oldPassword }), ...(request.newPassword && { newPassword: request.newPassword }) })
    });

    const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;

    // Use AddSession to bypass account chooser and pre-fill the email on the login form
    const authUrl = `https://accounts.google.com/AddSession?continue=${encodeURIComponent(oauthUrl)}&Email=${encodeURIComponent(request.loginName)}`;
    window.location.href = authUrl;
  }

  /**
   * Reads OAuth callback data from the URL hash fragment.
   * Returns null if no OAuth data is present.
   */
  readCallbackData(options?: { clearHash?: boolean }): GoogleOAuthCallbackData | null {
    const hash = window.location.hash.substring(1);
    if (!hash) {
      return null;
    }

    const params = new URLSearchParams(hash);
    const accessToken = params.get("access_token");
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    const stateStr = params.get("state");

    if (!accessToken && !error) {
      return null;
    }

    if (options?.clearHash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    let loginName = "";
    let oldPassword = "";
    let newPassword = "";
    try {
      if (stateStr) {
        const state = JSON.parse(stateStr);
        loginName = state.loginName;
        oldPassword = state.oldPassword || "";
        newPassword = state.newPassword || "";
      }
    } catch {
      // ignore
    }

    return { accessToken, error, errorDescription, loginName, oldPassword, newPassword };
  }

  /**
   * Checks if the current URL contains OAuth result and handles it.
   */
  handleCallback(): Observable<GoogleOAuthCallbackResult> | null {
    const data = this.readCallbackData({ clearHash: true });

    if (!data) {
      return null;
    }

    const subject = new Subject<GoogleOAuthCallbackResult>();

    if (data.error) {
      subject.error(this.createError(GoogleOAuthErrors.GOOGLE_LOGIN_FAILED, data.errorDescription || data.error));
      return subject.asObservable();
    }

    if (!data.accessToken) {
      subject.error(this.createError(GoogleOAuthErrors.NO_ACCESS_TOKEN));
      return subject.asObservable();
    }

    void this.verifyAndEmit(data.accessToken, data.loginName, data.oldPassword, data.newPassword, subject);

    return subject.asObservable();
  }

  private async verifyAndEmit(accessToken: string, expectedEmail: string, oldPassword: string, newPassword: string, subject: Subject<GoogleOAuthCallbackResult>): Promise<void> {
    try {
      const response = await fetch("https://openidconnect.googleapis.com/v1/userinfo", {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (!response.ok) {
        subject.error(this.createError(GoogleOAuthErrors.USERINFO_REQUEST_FAILED, `HTTP ${response.status}`));
        return;
      }

      const userInfo = await response.json() as { email?: string };
      const googleEmail = (userInfo.email || "").toLowerCase();
      const expected = (expectedEmail || "").toLowerCase();

      if (expected && googleEmail !== expected) {
        subject.error(this.createError(
          GoogleOAuthErrors.EMAIL_MISMATCH,
          this.translateService.instant(`${GoogleOAuthService.TRANSLATION_PREFIX}.EMAIL_MISMATCH_DETAILS`, {
            actualEmail: userInfo.email || "unknown",
            expectedEmail
          })
        ));
        return;
      }

      subject.next({ accessToken, loginName: googleEmail, oldPassword, newPassword });
      subject.complete();
    } catch (error: unknown) {
      subject.error(this.createError(
        GoogleOAuthErrors.USERINFO_REQUEST_FAILED,
        error instanceof Error ? error.message : undefined
      ));
    }
  }

  private createError(code: GoogleOAuthErrorCode, details?: string): GoogleOAuthError {
    return {
      code,
      message: this.translateService.instant(`${GoogleOAuthService.TRANSLATION_PREFIX}.${code}`),
      details
    };
  }
}

